import { OrderInstruction } from 'components/maps/BundleMap';
import SelectCourierModal from 'components/modals/SelectCourierModal';
import { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import { BundleJson } from 'types/bundleTypes';
import { Order, Region } from 'types/orderTypes';
import { BundleMapWindow } from './BundleMapWindow';
import PlannerBundlePlate from './PlannerBundlePlate';
import { ChangePickupPointModal } from '../../planner/tools/components/ChangePickupPointModal';

interface PlannerBundleMenusProps {
  orderInstructions: OrderInstruction[];
  bundle: BundleJson;
  mapOnTop?: boolean;
  monitorView?: boolean;
  region: Region;
  selectedDate?: Dayjs;
  monochrome?: boolean;
  expanded: boolean;
  recreateModeActive: boolean;
  movingToBundleActive: boolean;
  bundleDraftIsSelected: boolean;
  completedStatusList?: boolean;
  onMapSelected?: (bundle: BundleJson) => void;
  onSavedModification: () => void;
  onExpand?: (bundle: BundleJson, expanded: boolean) => void;
  toggleExpanded: () => void;
  startReordering: () => void;
  stopReordering: () => void;
  onSelectBundleDraft?: (id: string) => void;
  onDeselectBundleDraft?: (id: string) => void;
  startRecreating: () => void;
  stopRecreating: () => void;
  startMoving: () => void;
  stopMoving: () => void;
  setZonesOfBundles?: (newZonesOfBundles: {
    [bundleId: string]: string;
  }) => void;
  onOrdersUpdated: () => void;
}

export const PlannerBundleMenus = ({
  orderInstructions,
  bundle,
  mapOnTop,
  monitorView,
  monochrome,
  expanded,
  recreateModeActive,
  movingToBundleActive,
  bundleDraftIsSelected,
  completedStatusList,
  onMapSelected,
  onExpand,
  toggleExpanded,
  startReordering,
  stopReordering,
  onSelectBundleDraft,
  onDeselectBundleDraft,
  startRecreating,
  stopRecreating,
  startMoving,
  stopMoving,
  setZonesOfBundles,
  ...props
}: PlannerBundleMenusProps) => {
  const [showMap, setShowMap] = useState(false);
  const [showCourierModal, setShowCourierModal] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showChangePickupPointModal, setShowChangePickupPointModal] =
    useState(false);

  const orders = orderInstructions
    .map(orderInstruction => orderInstruction.order)
    .filter(order => order) as Order[];

  const onClickCloseMap = () => {
    setShowMap(false);
  };

  const onClickCloseCourierModal = () => {
    setShowCourierModal(false);
  };

  const onClickShowPickupPointModal = () => {
    setShowChangePickupPointModal(true);
  };

  const onSelectCourier = () => {
    props.onSavedModification();
    setShowCourierModal(false);
  };

  const onClickShowCourierModal = () => {
    setShowCourierModal(true);
  };

  const onClickShowMap = () => {
    setShowMap(true);
  };

  const handlePopoverClick = () => {
    setIsPopoverOpen(!isPopoverOpen);
  };

  const onCloseChangePickupPointModal = (changed: boolean) => {
    if (changed) {
      props.onOrdersUpdated();
    }
    setShowChangePickupPointModal(false);
  };

  return (
    <>
      <PlannerBundlePlate
        monitorView={monitorView}
        monochrome={monochrome}
        onExpand={onExpand}
        expanded={expanded}
        bundle={bundle}
        orderInstructions={orderInstructions}
        recreateModeActive={recreateModeActive}
        movingToBundleActive={movingToBundleActive}
        isPopoverOpen={isPopoverOpen}
        toggleExpanded={toggleExpanded}
        onClickShowCourierModal={onClickShowCourierModal}
        startReordering={startReordering}
        stopReordering={stopReordering}
        handlePopoverClick={handlePopoverClick}
        bundleDraftIsSelected={bundleDraftIsSelected}
        onSelectBundleDraft={onSelectBundleDraft}
        onDeselectBundleDraft={onDeselectBundleDraft}
        startRecreating={startRecreating}
        stopRecreating={stopRecreating}
        onClickShowMap={onClickShowMap}
        orders={orders}
        completedStatusList={completedStatusList}
        startMoving={startMoving}
        stopMoving={stopMoving}
        setZonesOfBundles={setZonesOfBundles}
        onClickShowPickupPointModal={onClickShowPickupPointModal}
      />
      {orderInstructions.length > 0 && showMap && (
        <BundleMapWindow
          bundleId={bundle.id}
          orderInstructions={orderInstructions}
          onClose={onClickCloseMap}
          onTop={mapOnTop}
          onSetFocus={() => onMapSelected && onMapSelected(bundle)}
          refreshZoomPosition={false}
          attachToBundlePlate={monitorView}
          region={props.region}
        />
      )}
      {showCourierModal && (
        <SelectCourierModal
          bundleId={bundle.id}
          onCloseCourierModal={onClickCloseCourierModal}
          onSelectedCourier={onSelectCourier}
          assignedCourierId={
            bundle.assignedCourierId
              ? parseInt(bundle.assignedCourierId)
              : undefined
          }
          region={bundle.region}
          selectedDate={props.selectedDate}
        />
      )}
      {showChangePickupPointModal && (
        <ChangePickupPointModal
          bundleId={bundle.id}
          bundleOrderIds={orders.map(order => order.id)}
          currentPickupAddress={orders[0].originAddress}
          region={bundle.region}
          onCloseModal={onCloseChangePickupPointModal}
        />
      )}
    </>
  );
};
